import { useCallback } from 'react';
import amplitudeLog from '@client/amplitude';
import { AMP_PROJECT_VIEW_HEADER_SBEF_GROUP_SELECT } from '@shared/constants';
import { SbefScopeGroupKey } from '@shared/construction/sbef-custom-groups.data';
import { ALL_SBEF_GROUP_KEYS } from '@features/sbef/sbef-group-options';
import {
  setSelectedSbefGroups,
  useSelectedSbefGroups,
} from '@features/sbef/sbef-groups.store';

/** Sentinel selection toggling every SBEF group at once. */
export const ALL_SBEF_GROUPS = 'All';
export type SbefGroupSelection = typeof ALL_SBEF_GROUPS | SbefScopeGroupKey;

/**
 * Toggle a single SBEF scope group (or all groups via {@link ALL_SBEF_GROUPS}) in the
 * persisted scope. Both "all selected" and "none selected" disable SBEF filtering.
 */
export const useSelectSbefGroup = (): ((value: SbefGroupSelection) => void) => {
  const selectedSbefGroups = useSelectedSbefGroups();
  return useCallback(
    (value: SbefGroupSelection) => {
      let newValues: SbefScopeGroupKey[];
      if (value === ALL_SBEF_GROUPS) {
        // Toggle between all groups selected and none (both disable SBEF filtering).
        newValues =
          selectedSbefGroups.length === ALL_SBEF_GROUP_KEYS.length
            ? []
            : [...ALL_SBEF_GROUP_KEYS];
      } else if (selectedSbefGroups.includes(value)) {
        newValues = selectedSbefGroups.filter((item) => item !== value);
      } else {
        newValues = [...selectedSbefGroups, value];
      }

      setSelectedSbefGroups(newValues);
      amplitudeLog(AMP_PROJECT_VIEW_HEADER_SBEF_GROUP_SELECT, {
        sbefGroups: newValues,
      });
    },
    [selectedSbefGroups],
  );
};
